import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AppContext } from '@edx/frontend-platform/react';
import { fetchCourseSummary } from '@blend-ed/blendxplatform/api';

/**
 * The course-summary endpoint returns completion data in a few different
 * shapes depending on backend version. Flatten any of them into a simple
 * { [courseId]: percentComplete } map.
 */
const buildProgressByCourseId = (summary) => {
  if (!summary) return {};
  const candidates = [];
  if (Array.isArray(summary)) candidates.push(...summary);
  if (Array.isArray(summary?.courses)) candidates.push(...summary.courses);
  if (Array.isArray(summary?.course_summaries)) candidates.push(...summary.course_summaries);
  if (candidates.length === 0 && typeof summary === 'object') {
    const values = Object.values(summary);
    if (values.every((v) => typeof v === 'object' && v !== null)) {
      candidates.push(...values);
    }
  }
  return candidates.reduce((acc, item) => {
    const courseId = item?.course_id || item?.courseId || item?.id || item?.course_key;
    if (!courseId) return acc;
    const percent = item?.complete_percentage
      ?? item?.completion_percentage
      ?? item?.percent_complete
      ?? item?.progress_percent
      ?? item?.progressPercent;
    if (typeof percent === 'number' && !Number.isNaN(percent)) {
      acc[courseId] = percent;
    }
    return acc;
  }, {});
};

export const useProgramProgress = () => {
  const { authenticatedUser } = useContext(AppContext);
  const userId = authenticatedUser?.userId || authenticatedUser?.id || authenticatedUser?.user_id || null;

  const { data } = useQuery({
    queryKey: ['courseSummary', userId],
    queryFn: () => fetchCourseSummary(userId),
    enabled: !!userId,
  });

  return { progressByCourseId: buildProgressByCourseId(data) };
};
